import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, AlertCircle, ChevronDown } from 'lucide-react';
import '../styles/RedeemForm.css';

const FormStatus = {
    IDLE: 'idle',
    LOADING: 'loading',
    SUCCESS: 'success',
    ERROR: 'error'
};

// Sample product list - replace with your actual products
const PRODUCTS = [
    { id: 1, name: 'Premium Software License', code: 'PSL' },
    { id: 2, name: 'Digital Content Pack', code: 'DCP' },
    { id: 3, name: 'Extended Support Plan', code: 'ESP' },
    { id: 4, name: 'Professional Tools Suite', code: 'PTS' },
    { id: 5, name: 'Creative Assets Bundle', code: 'CAB' },
    { id: 6, name: 'Training Course Access', code: 'TCA' },
    { id: 7, name: 'Cloud Storage Upgrade', code: 'CSU' },
    { id: 8, name: 'Mobile App Premium', code: 'MAP' }
];

const RedeemForm = () => {
    const [values, setValues] = useState({ code: '', product: '', email: '' });
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState(FormStatus.IDLE);
    const [errorMessage, setErrorMessage] = useState('');

    // Product dropdown state
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [productSearch, setProductSearch] = useState('');
    const [filteredProducts, setFilteredProducts] = useState(PRODUCTS);

    const validateForm = () => {
        const newErrors = {};

        if (!values.code.trim()) {
            newErrors.code = 'Redemption code is required';
        } else if (values.code.length < 6) {
            newErrors.code = 'Redemption code must be at least 6 characters';
        }

        if (!values.product.trim()) {
            newErrors.product = 'Product selection is required';
        }

        if (!values.email.trim()) {
            newErrors.email = 'Email address is required';
        } else if (!/\S+@\S+\.\S+/.test(values.email)) {
            newErrors.email = 'Email address is invalid';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setValues(prev => ({ ...prev, [name]: value }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleProductSearch = (e) => {
        const searchValue = e.target.value;
        setProductSearch(searchValue);
        setValues(prev => ({ ...prev, product: searchValue }));

        // Filter products based on search
        const filtered = PRODUCTS.filter(product =>
            product.name.toLowerCase().includes(searchValue.toLowerCase()) ||
            product.code.toLowerCase().includes(searchValue.toLowerCase())
        );
        setFilteredProducts(filtered);

        if (!isDropdownOpen) {
            setIsDropdownOpen(true);
        }

        // Clear product error when user starts typing
        if (errors.product) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.product;
                return newErrors;
            });
        }
    };

    const handleProductSelect = (product) => {
        setValues(prev => ({ ...prev, product: product.name }));
        setProductSearch(product.name);
        setIsDropdownOpen(false);

        // Clear product error when selection is made
        if (errors.product) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.product;
                return newErrors;
            });
        }
    };

    const handleDropdownToggle = () => {
        setIsDropdownOpen(!isDropdownOpen);
        if (!isDropdownOpen) {
            setFilteredProducts(PRODUCTS);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setStatus(FormStatus.LOADING);

        // Simulate API call - replace with actual backend call
        try {
            // Simulated API delay
            await new Promise(resolve => setTimeout(resolve, 1500));

            // For demonstration: randomly succeed or fail
            if (Math.random() > 0.5) {
                setStatus(FormStatus.SUCCESS);
            } else {
                throw new Error('Invalid redemption code');
            }
        } catch (error) {
            setStatus(FormStatus.ERROR);
            setErrorMessage(error.message || 'Something went wrong');
        }
    };

    const resetForm = () => {
        setValues({ code: '', product: '', email: '' });
        setErrors({});
        setStatus(FormStatus.IDLE);
        setErrorMessage('');
        setProductSearch('');
        setIsDropdownOpen(false);
        setFilteredProducts(PRODUCTS);
    };

    return (
        <div className="redeem-form-container">
            <motion.div
                className="form-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <div className="form-header">
                    <h2>Redeem Your Code</h2>
                    <p>Enter your code and email address below to redeem your product</p>
                </div>

                {status === FormStatus.SUCCESS ? (
                    <motion.div
                        className="success-message"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3 }}
                    >
                        <div className="success-icon">
                            <Check size={32} />
                        </div>
                        <h3>Success!</h3>
                        <p>Your code has been redeemed successfully. We've sent the details to your email address.</p>
                        <button
                            className="reset-button"
                            onClick={resetForm}
                        >
                            Redeem Another Code
                        </button>
                    </motion.div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label htmlFor="code">Redemption Code</label>
                            <input
                                type="text"
                                id="code"
                                name="code"
                                value={values.code}
                                onChange={handleChange}
                                placeholder="Enter your code"
                                className={errors.code ? 'error' : ''}
                                disabled={status === FormStatus.LOADING}
                            />
                            {errors.code && <p className="error-text">{errors.code}</p>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="product">Product</label>
                            <div className="dropdown-container">
                                <div className="dropdown-input-wrapper">
                                    <input
                                        type="text"
                                        id="product"
                                        name="product"
                                        value={productSearch}
                                        onChange={handleProductSearch}
                                        onFocus={() => setIsDropdownOpen(true)}
                                        placeholder="Search and select a product"
                                        className={errors.product ? 'error' : ''}
                                        disabled={status === FormStatus.LOADING}
                                        autoComplete="off"
                                    />
                                    <button
                                        type="button"
                                        className="dropdown-toggle"
                                        onClick={handleDropdownToggle}
                                        disabled={status === FormStatus.LOADING}
                                    >
                                        <ChevronDown
                                            size={18}
                                            style={{
                                                transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                                transition: 'transform 0.2s ease'
                                            }}
                                        />
                                    </button>
                                </div>

                                {isDropdownOpen && (
                                    <motion.div
                                        className="dropdown-menu"
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        {filteredProducts.length > 0 ? (
                                            filteredProducts.map((product) => (
                                                <div
                                                    key={product.id}
                                                    className="dropdown-item"
                                                    onClick={() => handleProductSelect(product)}
                                                >
                                                    <div className="product-name">{product.name}</div>
                                                    <div className="product-code">{product.code}</div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="dropdown-item no-results">
                                                No products found
                                            </div>
                                        )}
                                    </motion.div>
                                )}
                            </div>
                            {errors.product && <p className="error-text">{errors.product}</p>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="email">Email Address</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={values.email}
                                onChange={handleChange}
                                placeholder="your@email.com"
                                className={errors.email ? 'error' : ''}
                                disabled={status === FormStatus.LOADING}
                            />
                            {errors.email && <p className="error-text">{errors.email}</p>}
                        </div>

                        {status === FormStatus.ERROR && (
                            <motion.div
                                className="error-message"
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                transition={{ duration: 0.3 }}
                            >
                                <AlertCircle size={18} />
                                <p>{errorMessage}</p>
                            </motion.div>
                        )}

                        <button
                            type="submit"
                            className={`submit-button ${status === FormStatus.LOADING ? 'loading' : ''}`}
                            disabled={status === FormStatus.LOADING}
                        >
                            {status === FormStatus.LOADING ? (
                                <span className="loading-spinner"></span>
                            ) : (
                                'Redeem Now'
                            )}
                        </button>
                    </form>
                )}
            </motion.div>
        </div>
    );
};

export default RedeemForm;